import React, { useState, useEffect, useMemo } from "react";
import { SearchModal } from "~/widgets/SearchModal";
import { Badge, type BadgeVariant } from "~/widgets/Badge/Badge";
import styles from "~/widgets/GlobalCommandPalette/GlobalCommandPalette.module.scss";
import { useFocusContext } from "~/contexts/FocusContext";
import { AppCommand } from "~/types/commands";
import { ActionDefinition } from "~/types/actions";

// Extended action type that includes context targeting information
type GlobalAction = ActionDefinition & {
  contextId?: string;
};

const GlobalCommandPalette: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { focusStack, dispatchCommand, setIsOverlayOpen } = useFocusContext();

  // Notify FocusContext when overlay opens/closes
  useEffect(() => {
    setIsOverlayOpen(isOpen);
  }, [isOpen, setIsOverlayOpen]);

  // Listen for the "/" key to toggle the palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger if no input/textarea is focused (unless it's the palette itself)
      const target = e.target as HTMLElement;
      const isInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

      if (e.key === "/" && !isInput) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleClose = () => setIsOpen(false);

  // Derive available actions based on focus stack
  const availableActions = useMemo(() => {
    const actions: GlobalAction[] = [];

    // Process stack via hierarchy (Active -> Parent -> Parent...)
    const itemsToCheck: typeof focusStack = [];
    if (focusStack.length > 0) {
      let currentItem: (typeof focusStack)[0] | undefined = focusStack[focusStack.length - 1];
      while (currentItem) {
        itemsToCheck.push(currentItem);
        const pid: string | undefined = currentItem.parentId;
        currentItem = pid ? focusStack.find((i) => i.id === pid) : undefined;
      }
    }

    itemsToCheck.forEach((item) => {
      if (item.actions) {
        // Map provided actions to this specific instance
        const instanceActions = item.actions.map((action) => ({
          ...action,
          contextId: item.id,
          // If we have a name in meta, append it to clarify target
          label: item.meta.name
            ? `${action.label} (${item.meta.name})`
            : action.label,
        }));
        actions.push(...instanceActions);
      }
    });

    return actions;
  }, [focusStack]);

  const filterFn = (action: GlobalAction, query: string): boolean => {
    const term = query.toLowerCase();
    return (
      action.label.toLowerCase().includes(term) ||
      action.description.toLowerCase().includes(term) ||
      action.keywords.some((kw) => kw.includes(term))
    );
  };

  const handleSelect = (action: GlobalAction) => {
    // Construct strongly typed command payload
    // We cast action.id to the specific command string literal type
    const command = {
      type: action.id,
      payload: {},
    } as AppCommand;

    // Dispatch to the specific context instance if identified
    dispatchCommand(command, action.contextId);

    handleClose();
  };

  const getCategoryVariant = (category: GlobalAction["category"]): BadgeVariant => {
    if (!category) return "category";
    return category as BadgeVariant;
  };

  const formatShortcut = (s: string) => {
    let key = s;
    if (s === "ArrowUp") key = "↑";
    if (s === "ArrowDown") key = "↓";
    return key;
  };

  const renderItem = (action: GlobalAction, isSelected: boolean) => {
    return (
      <div className={styles.commandContent}>
        <div className={styles.commandInfo}>
          <div className={styles.labelRow}>
            <span
              className={`${styles.commandLabel} ${isSelected ? styles.commandLabelSelected : ""} `}
            >
              {action.label}
            </span>
            <Badge variant={getCategoryVariant(action.category)} selected={isSelected}>
              {action.category || "general"}
            </Badge>
          </div>
          <span className={styles.commandDescription}>{action.description}</span>
        </div>
        {action.shortcut && (
          <div className={styles.shortcutsWrapper}>
            {(Array.isArray(action.shortcut) ? action.shortcut : [action.shortcut]).map((s, i, arr) => (
              <React.Fragment key={i}>
                <kbd
                  className={`${styles.shortcutBadge} ${isSelected ? styles.shortcutBadgeSelected : ""} `}
                >
                  {formatShortcut(s)}
                </kbd>
                {i < arr.length - 1 && (
                  <span className={styles.shortcutSeparator}>/</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <SearchModal<GlobalAction>
      isOpen={isOpen}
      onClose={handleClose}
      placeholder="Type a command..."
      items={availableActions}
      getItemKey={(action) => action.id}
      filterFn={filterFn}
      renderItem={renderItem}
      onSelect={handleSelect}
      emptyMessage="No commands found"
      emptyHint={focusStack.length === 0 ? "No active context found" : "Try a different search term"}
      footerHints={[
        { label: "navigate", hint: "↑↓" },
        { label: "execute", hint: "↵" },
        { label: "close", hint: "Esc" },
      ]}
    />
  );
};

export default GlobalCommandPalette;
